import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchSetting, saveSetting, type VerificationRules } from "@/lib/super-admin";

export const Route = createFileRoute("/_authenticated/super-admin/verification-rules")({
  head: () => ({
    meta: [
      { title: "Verification rules · Landoo Super Admin" },
      { name: "description", content: "Choose which verification steps partners must complete." },
    ],
  }),
  component: VerificationRulesPage,
});

const FIELDS: { key: keyof VerificationRules; label: string; hint: string }[] = [
  {
    key: "require_mobile_otp",
    label: "Mobile OTP",
    hint: "Verify the partner's mobile number by OTP.",
  },
  { key: "require_identity", label: "Identity document", hint: "Upload a government ID document." },
  { key: "require_selfie", label: "Selfie match", hint: "Selfie compared with identity document." },
  {
    key: "require_video_call",
    label: "Video verification call",
    hint: "Admin schedules a live video call.",
  },
  {
    key: "require_property_docs",
    label: "Property documents",
    hint: "Ownership or authorisation documents for the listing.",
  },
  {
    key: "auto_approve_returning_partners",
    label: "Auto-approve returning partners",
    hint: "Skip repeat steps for partners already verified for another service.",
  },
];

const DEFAULTS: VerificationRules = {
  require_mobile_otp: true,
  require_identity: true,
  require_selfie: true,
  require_video_call: false,
  require_property_docs: true,
  auto_approve_returning_partners: false,
};

function VerificationRulesPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["super-admin", "verification-rules"],
    queryFn: () => fetchSetting<VerificationRules>("verification_rules"),
  });
  const [rules, setRules] = useState<VerificationRules>(DEFAULTS);
  useEffect(() => {
    if (data) setRules({ ...DEFAULTS, ...data });
  }, [data]);

  const save = async () => {
    try {
      await saveSetting("verification_rules", rules);
      toast.success("Verification rules saved");
      qc.invalidateQueries({ queryKey: ["super-admin", "verification-rules"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    }
  };

  return (
    <DashboardShell title="Verification rules" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <p className="mb-4 text-xs text-muted-foreground">
        These rules apply to new partner verification requests. Existing partners keep their current
        verification status.
      </p>
      <div className="space-y-2">
        {FIELDS.map((f) => (
          <label
            key={f.key}
            className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft"
          >
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0"
              checked={rules[f.key]}
              onChange={(e) => setRules({ ...rules, [f.key]: e.target.checked })}
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-brown-dark">{f.label}</div>
              <p className="text-xs text-muted-foreground">{f.hint}</p>
            </div>
          </label>
        ))}
      </div>
      <button
        onClick={save}
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-xs font-semibold text-white shadow-soft"
      >
        <Save className="h-4 w-4" /> Save rules
      </button>
    </DashboardShell>
  );
}
